import { FC, useCallback, useEffect, useState } from 'react';
import { Input, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import useTwoFaLockout from 'hooks/useTwoFaLockout';
import {
  isRecoveryCode,
  isTotpCode,
  normalizeRecoveryCode,
  sanitizeRecoveryCodeInput
} from 'utils/recoveryCode';

const { Paragraph, Text } = Typography;

interface IProps {
  open: boolean;
  title: string;
  description: string;
  okText: string;
  /**
   * Disable accepts a recovery code. Re-issuing the set does NOT — a
   * leaked recovery code must not be spendable to mint ten more — so
   * that caller keeps the input to six digits.
   */
  allowRecoveryCode?: boolean;
  onSubmit: (otp: string) => Promise<void>;
  onClose: () => void;
}

const TwoFaCodeModal: FC<IProps> = ({
  open,
  title,
  description,
  okText,
  allowRecoveryCode = false,
  onSubmit,
  onClose
}) => {
  const { t } = useTranslation();
  const { isLocked, readError } = useTwoFaLockout();

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setCode('');
      setError('');
      setLoading(false);
    }
  }, [open]);

  const isValid = allowRecoveryCode
    ? isTotpCode(code) || isRecoveryCode(code)
    : isTotpCode(code);

  const onConfirm = useCallback(async () => {
    if (!isValid) {
      setError(t('profile.2fa.code_invalid'));

      return;
    }

    try {
      setLoading(true);
      // Send the clean form when it is a recovery code: the server
      // strips punctuation anyway, and this keeps what we validate and
      // what we send identical.
      await onSubmit(isRecoveryCode(code) ? normalizeRecoveryCode(code) : code);
    } catch (err) {
      // The backend localizes these, and `readError` starts the lockout
      // when it reports one.
      setError(readError(err).message);
    } finally {
      setLoading(false);
    }
  }, [code, isValid, onSubmit, readError, t]);

  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      onOk={onConfirm}
      onCancel={onClose}
      confirmLoading={isLoading}
      okButtonProps={{
        disabled: isLocked,
        'data-test-id': '2fa-code-submit-button'
      }}
      destroyOnClose
    >
      <Paragraph>{description}</Paragraph>
      <Input
        value={code}
        status={error ? 'error' : ''}
        maxLength={allowRecoveryCode ? 11 : 6}
        placeholder={allowRecoveryCode ? '123456 / XXXXX-XXXXX' : '123456'}
        onChange={e => {
          setError('');
          setCode(
            allowRecoveryCode
              ? sanitizeRecoveryCodeInput(e.target.value)
              : e.target.value.replace(/\D/g, '').slice(0, 6)
          );
        }}
        data-test-id="2fa-code-input"
      />
      {!!error && (
        <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
          {error}
        </Text>
      )}
    </Modal>
  );
};

export default TwoFaCodeModal;
